import React, { useEffect, useState } from 'react';
import { api } from '../utils/api';

const DEFAULT_CALL_SCRIPTS = api.DEFAULT_CALL_SCRIPTS || {};

export default function Scripts() {
  const [tab, setTab] = useState('email');
  const [settings, setSettings] = useState(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.getSettings().then(s => {
      setSettings({
        emailScript: s.emailScript || api.DEFAULT_EMAIL_SCRIPT || '',
        callScriptGoogleReview: s.callScriptGoogleReview || DEFAULT_CALL_SCRIPTS['Google Review Agent'] || '',
        callScriptWebsite: s.callScriptWebsite || DEFAULT_CALL_SCRIPTS['Website Development'] || '',
        callScriptChatbot: s.callScriptChatbot || DEFAULT_CALL_SCRIPTS['Chatbot (Carl)'] || '',
        callScriptAdmin: s.callScriptAdmin || DEFAULT_CALL_SCRIPTS['Automated Admin Assistant'] || '',
        // carry over other settings so we don't wipe them on save
        _full: s,
      });
    }).catch(e => setError(e.message));
  }, []);

  const handleSave = async () => {
    setSaved(false);
    setError('');
    try {
      await api.saveSettings({
        ...(settings._full || {}),
        emailScript: settings.emailScript,
        callScriptGoogleReview: settings.callScriptGoogleReview,
        callScriptWebsite: settings.callScriptWebsite,
        callScriptChatbot: settings.callScriptChatbot,
        callScriptAdmin: settings.callScriptAdmin,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(e.message);
    }
  };

  if (!settings) return <div className="text-gray-400">Loading scripts...</div>;

  const textareaClass = 'w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-blue-500 leading-relaxed';

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Scripts</h2>
        <button
          onClick={handleSave}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors"
        >
          Save Scripts
        </button>
      </div>

      {error && <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4 text-sm text-red-400">{error}</div>}
      {saved && <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 mb-4 text-sm text-green-400">Scripts saved!</div>}

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-900 rounded-lg p-1 w-fit">
        {[
          { key: 'email', label: 'Email Script' },
          { key: 'calls', label: 'Call Scripts' },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-5 py-2 rounded-md text-sm font-medium transition-colors ${
              tab === t.key ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'email' && (
        <div className="max-w-2xl">
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
            <h3 className="text-lg font-semibold mb-2">Email Framework</h3>
            <p className="text-xs text-gray-500 mb-4">
              This framework is given to the AI as structural instructions for every email generated.
              Edit it to change how emails are written — the AI will follow this blueprint.
            </p>
            <textarea
              value={settings.emailScript}
              onChange={e => setSettings({ ...settings, emailScript: e.target.value })}
              rows={10}
              className={textareaClass}
            />
            <div className="mt-3 p-3 bg-gray-800/50 rounded-lg">
              <p className="text-xs text-gray-500 font-medium mb-1">Current framework:</p>
              <p className="text-xs text-gray-400">{settings.emailScript || '(empty)'}</p>
            </div>
          </div>
        </div>
      )}

      {tab === 'calls' && (
        <div className="max-w-2xl space-y-5">
          <p className="text-sm text-gray-400">Use these scripts when calling prospects as a follow-up to an email. Copy and read naturally — adjust on the fly.</p>

          {[
            { key: 'callScriptGoogleReview', label: 'Google Review Agent', color: 'text-yellow-400' },
            { key: 'callScriptWebsite', label: 'Website Development', color: 'text-blue-400' },
            { key: 'callScriptChatbot', label: 'Chatbot (Carl)', color: 'text-purple-400' },
            { key: 'callScriptAdmin', label: 'Automated Admin Assistant', color: 'text-emerald-400' },
          ].map(({ key, label, color }) => (
            <div key={key} className="bg-gray-900 border border-gray-800 rounded-xl p-5">
              <h3 className={`text-base font-semibold mb-3 ${color}`}>{label}</h3>
              <textarea
                value={settings[key]}
                onChange={e => setSettings({ ...settings, [key]: e.target.value })}
                rows={4}
                className={textareaClass}
              />
              <button
                onClick={() => {
                  navigator.clipboard.writeText(settings[key]);
                }}
                className="mt-2 text-xs px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg transition-colors"
              >
                Copy Script
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
